/**
 * Enhanced Token Manager with Activity-Based Session Management
 *
 * This service implements:
 * 1. Automatic logout on token expiration
 * 2. Activity-based token renewal (idle timeout)
 * 3. Global token monitoring across all pages
 * 4. Uniform behavior across the entire application
 * 5. Persistent token storage in localStorage (survives server restarts)
 * 6. 30-minute idle timeout (configurable)
 *
 * Token Storage Strategy:
 * - Access token stored in localStorage for persistence across server restarts
 * - Refresh token stored in httpOnly cookie by backend (most secure)
 * - Activity timestamp tracked for idle timeout
 */

import { api } from './api';

interface TokenData {
  access: string;
  access_expires_at: string;
}

interface SessionConfig {
  // Token will expire after this many minutes of inactivity
  idleTimeoutMinutes: number;
  // Check token validity every X seconds
  checkIntervalSeconds: number;
  // Renew token when less than X minutes remain
  renewBeforeMinutes: number;
  // Show warning when less than X minutes remain
  warnBeforeMinutes: number;
}

// Storage keys
const STORAGE_KEYS = {
  TOKEN_DATA: 'connectpath_token_data',
  USER_DATA: 'connectpath_user_data',
  LAST_ACTIVITY: 'connectpath_last_activity',
} as const;

// Ensure we always use the same global instance
declare global {
  interface Window {
    __enhancedTokenManagerInstance?: EnhancedTokenManager;
  }
}

export class EnhancedTokenManager {
  private accessToken: string | null = null;
  private expiresAt: Date | null = null;
  private lastActivity: Date | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private expirationCheckTimer: NodeJS.Timeout | null = null;
  private activityListeners: Set<() => void> = new Set();
  private refreshPromise: Promise<TokenData> | null = null;
  private initialized: boolean = false;
  
  // Configuration - 30 minute idle timeout as requested
  private config: SessionConfig = {
    idleTimeoutMinutes: 30, // 30 minutes of inactivity = logout
    checkIntervalSeconds: 30, // Check every 30 seconds
    renewBeforeMinutes: 5, // Renew 5 minutes before expiration
    warnBeforeMinutes: 5, // Warn 5 minutes before expiration
  };

  private constructor() {
    console.log('[TokenManager] Constructor called - this should only happen once!');
    // Double-check if instance already exists on window
    if (window.__enhancedTokenManagerInstance && window.__enhancedTokenManagerInstance !== this) {
      console.warn('[TokenManager] WARNING: Instance already exists on window!');
    }
  }

  static getInstance(): EnhancedTokenManager {
    // Use a more robust key that's less likely to be cleared
    const INSTANCE_KEY = Symbol.for('ConnectPath.EnhancedTokenManager');
    const globalThis = window as any;
    
    // Check if instance exists using Symbol
    if (!globalThis[INSTANCE_KEY]) {
      // Also check the old key for backward compatibility
      if (window.__enhancedTokenManagerInstance) {
        console.log('[TokenManager] Migrating from old instance key');
        globalThis[INSTANCE_KEY] = window.__enhancedTokenManagerInstance;
      } else {
        console.log('[TokenManager] Creating new instance');
        const instance = new EnhancedTokenManager();
        globalThis[INSTANCE_KEY] = instance;
        window.__enhancedTokenManagerInstance = instance; // Keep for debugging
        instance.initialize();
      }
    }
    
    return globalThis[INSTANCE_KEY];
  }

  /**
   * Initialize the token manager
   * Uses localStorage for persistence across server restarts and page reloads
   */
  private initialize() {
    if (this.initialized) {
      console.log('[TokenManager] Already initialized, skipping');
      return;
    }

    console.log('[TokenManager] initialize() called');
    this.initialized = true;

    // Try to restore token from localStorage (persists across server restarts)
    try {
      const storedData = localStorage.getItem(STORAGE_KEYS.TOKEN_DATA);
      const storedActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);

      if (storedData) {
        const { token, expiresAt } = JSON.parse(storedData);
        const expiry = new Date(expiresAt);
        const now = new Date();

        // Restore last activity time
        const lastActivityTime = storedActivity ? new Date(storedActivity) : now;
        const idleTimeMs = now.getTime() - lastActivityTime.getTime();
        const idleTimeoutMs = this.config.idleTimeoutMinutes * 60 * 1000;

        // Check both token expiry AND idle timeout
        const tokenValid = expiry > now;
        const notIdle = idleTimeMs < idleTimeoutMs;

        if (tokenValid && notIdle) {
          this.accessToken = token;
          this.expiresAt = expiry;
          this.lastActivity = lastActivityTime;

          console.log('[TokenManager] Restored token from localStorage', {
            expiresIn: Math.round((expiry.getTime() - now.getTime()) / 1000 / 60) + ' minutes',
            idleFor: Math.round(idleTimeMs / 1000 / 60) + ' minutes',
            idleTimeout: this.config.idleTimeoutMinutes + ' minutes'
          });

          // Update activity to now since user is actively using the app
          this.updateActivity();

          // Start monitoring the restored token
          this.startExpirationMonitoring();
        } else {
          const reason = !tokenValid ? 'token expired' : 'idle timeout exceeded';
          console.log(`[TokenManager] Stored token invalid: ${reason}, clearing`);
          this.clearStoredData();
        }
      }
    } catch (e) {
      console.error('[TokenManager] Failed to restore token:', e);
      this.clearStoredData();
    }

    // Track user activity - this is safe to do multiple times
    this.setupActivityTracking();
  }

  /**
   * Clear all stored data from localStorage
   */
  private clearStoredData() {
    try {
      localStorage.removeItem(STORAGE_KEYS.TOKEN_DATA);
      localStorage.removeItem(STORAGE_KEYS.LAST_ACTIVITY);
      // Keep user data for now - cleared separately on logout
    } catch (e) {
      console.error('[TokenManager] Failed to clear stored data:', e);
    }
  }

  /**
   * Set up activity tracking to detect user interaction
   */
  private setupActivityTracking() {
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click'
    ];
    
    const activityHandler = () => {
      this.updateActivity();
    };
    
    // Throttle activity updates to once per second
    let activityTimeout: NodeJS.Timeout | null = null;
    const throttledHandler = () => {
      if (!activityTimeout) {
        activityTimeout = setTimeout(() => {
          activityHandler();
          activityTimeout = null;
        }, 1000);
      }
    };
    
    // Add listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, throttledHandler, { passive: true });
    });
  }

  /**
   * Update last activity timestamp
   * Stored separately in localStorage for reliable idle timeout tracking
   */
  updateActivity() {
    this.lastActivity = new Date();

    // Store activity timestamp in localStorage (separate from token for reliability)
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_ACTIVITY, this.lastActivity.toISOString());
    } catch (e) {
      // Silent fail - activity tracking is not critical
    }

    // Notify listeners
    this.activityListeners.forEach(listener => listener());
  }

  /**
   * Start monitoring token expiration
   */
  private startExpirationMonitoring() {
    // Clear existing timer
    if (this.expirationCheckTimer) {
      clearInterval(this.expirationCheckTimer);
    }
    
    // Only start monitoring if we have a token
    if (!this.accessToken) {
      console.log('[TokenManager] No token to monitor');
      return;
    }
    
    // Check token validity periodically
    this.expirationCheckTimer = setInterval(() => {
      this.checkTokenValidity();
    }, this.config.checkIntervalSeconds * 1000);
    
    // Don't check immediately - wait for first interval
    // This prevents race conditions during navigation
    console.log('[TokenManager] Started expiration monitoring');
  }

  /**
   * Check if token is still valid (not expired and not idle)
   */
  private checkTokenValidity() {
    if (!this.accessToken || !this.expiresAt) {
      return;
    }

    // Initialize lastActivity if not set (happens after navigation or page reload)
    // FIXED: Try to restore from localStorage first, only use current time if not available
    if (!this.lastActivity) {
      const storedActivity = localStorage.getItem(STORAGE_KEYS.LAST_ACTIVITY);
      if (storedActivity) {
        this.lastActivity = new Date(storedActivity);
        console.log('[TokenManager] Restored lastActivity from localStorage:', this.lastActivity.toISOString());
      } else {
        console.log('[TokenManager] No stored lastActivity, initializing to now');
        this.lastActivity = new Date();
        this.updateActivity();
      }
      return; // Skip this check, will check on next interval
    }

    const now = Date.now();
    const tokenExpired = this.expiresAt.getTime() <= now;

    // Check token expiration
    if (tokenExpired) {
      console.log(`[TokenManager] Token expired at ${this.expiresAt.toISOString()}`);
      this.handleExpiredToken();
      return;
    }

    // Check idle timeout (30 minutes of inactivity)
    const idleTimeMs = now - this.lastActivity.getTime();
    const idleTimeoutMs = this.config.idleTimeoutMinutes * 60 * 1000;
    const idleWarnThresholdMs = this.config.warnBeforeMinutes * 60 * 1000; // Warn 5 min before idle logout

    if (idleTimeMs >= idleTimeoutMs) {
      console.log(`[TokenManager] Idle timeout exceeded: ${Math.round(idleTimeMs / 1000 / 60)} minutes idle`);
      this.handleExpiredToken();
      return;
    }

    // Warn user when approaching idle timeout (5 minutes before)
    const timeUntilIdleLogout = idleTimeoutMs - idleTimeMs;
    if (timeUntilIdleLogout <= idleWarnThresholdMs && timeUntilIdleLogout > 0) {
      this.emitExpirationWarning(Math.floor(timeUntilIdleLogout / 1000));
    }

    // Background token refresh if token is expiring soon (keeps token fresh while user is active)
    // FIXED: Don't start refresh if one is already in progress (prevents race condition)
    const timeUntilExpiry = this.expiresAt.getTime() - now;
    const renewThreshold = this.config.renewBeforeMinutes * 60 * 1000;

    if (timeUntilExpiry < renewThreshold && timeUntilExpiry > 0 && !this.refreshPromise) {
      console.log('[TokenManager] Token expiring soon, attempting background renewal...');
      // Fire and forget - the refresh will update the token if successful
      // If it fails, the token is still valid until it actually expires
      this.refreshToken().catch(err => {
        // Only log, don't take action - the 401 handler or expiry check will handle it
        console.warn('[TokenManager] Background token refresh failed:', err.message || err);
      });
    }
  }

  /**
   * Handle expired token - clear and redirect (only for protected routes)
   */
  private handleExpiredToken() {
    this.clearToken();

    // Emit expiration event
    window.dispatchEvent(new CustomEvent('sessionExpired', {
      detail: { reason: 'Token expired or session idle' }
    }));

    // Only redirect to login if not on a public route
    // Member PWA routes use separate PIN-based auth, not platform tokens
    const currentPath = window.location.pathname;
    const publicRoutes = [
      '/member/pwa',
      '/member/portal',
      '/member/pin',
      '/assessment/public',
      '/login',
      '/register',
      '/forgot-password',
      '/reset-password',
      '/health',
      '/platform-health',
      '/platform-admin/login',
      '/'
    ];

    const isPublicRoute = publicRoutes.some(route =>
      currentPath === route || currentPath.startsWith(route + '/')
    );

    if (!isPublicRoute) {
      // Check if user is in Platform Admin section - redirect to platform admin login
      if (currentPath.startsWith('/platform-admin')) {
        console.log('[TokenManager] Token expired in Platform Admin, redirecting to platform-admin login');
        window.location.href = '/platform-admin/login';
      } else {
        // Force redirect to login for protected routes
        window.location.href = '/login';
      }
    } else {
      console.log('[TokenManager] Token expired on public route, skipping redirect');
    }
  }

  /**
   * Set token with activity tracking
   * Includes validation to handle missing or invalid expiresAt values
   * Stores in localStorage for persistence across server restarts
   */
  setToken(token: string, expiresAt: string) {
    // Default expiry: 30 minutes from now (matches backend ACCESS_TOKEN_LIFETIME)
    const DEFAULT_EXPIRY_MS = 30 * 60 * 1000;

    // Validate expiresAt - critical fix for token refresh issues
    let validExpiresAt = expiresAt;
    if (!expiresAt) {
      console.error('[TokenManager] setToken called without expiresAt - using default 30min');
      validExpiresAt = new Date(Date.now() + DEFAULT_EXPIRY_MS).toISOString();
    }

    // Parse and validate the date
    let expiry = new Date(validExpiresAt);
    if (isNaN(expiry.getTime())) {
      console.error('[TokenManager] Invalid expiresAt format:', expiresAt, '- using default 30min');
      expiry = new Date(Date.now() + DEFAULT_EXPIRY_MS);
      validExpiresAt = expiry.toISOString();
    }

    this.accessToken = token;
    this.expiresAt = expiry;
    this.updateActivity(); // Reset activity on new token

    console.log('[TokenManager] Token set, expires at:', this.expiresAt.toISOString());

    // Store token data in localStorage for persistence across server restarts
    try {
      localStorage.setItem(STORAGE_KEYS.TOKEN_DATA, JSON.stringify({
        token: token,
        expiresAt: validExpiresAt
      }));
    } catch (e) {
      console.error('[TokenManager] Failed to store token data:', e);
    }

    // Restart expiration monitoring
    this.startExpirationMonitoring();

    // Emit token refresh event
    window.dispatchEvent(new CustomEvent('tokenRefreshed', {
      detail: { expiresAt: this.expiresAt }
    }));
  }

  /**
   * Get current access token
   * FIXED: Now properly waits for refresh if token is expiring
   */
  async getToken(): Promise<string | null> {
    // If no token, return null immediately
    if (!this.accessToken) {
      return null;
    }

    // If a refresh is already in progress, wait for it
    if (this.refreshPromise) {
      try {
        await this.refreshPromise;
      } catch (e) {
        // Refresh failed, token will be cleared
        return null;
      }
    }

    // Check if token needs refresh (expiring within 2 minutes)
    if (this.expiresAt) {
      const timeUntilExpiry = this.expiresAt.getTime() - Date.now();
      const urgentRenewThreshold = 2 * 60 * 1000; // 2 minutes - more aggressive for API calls

      if (timeUntilExpiry < urgentRenewThreshold && timeUntilExpiry > 0 && !this.refreshPromise) {
        console.log('[TokenManager] Token expiring soon, refreshing before API call...');
        try {
          await this.refreshToken();
        } catch (e) {
          // If refresh fails but token hasn't expired yet, still return it
          if (timeUntilExpiry > 0) {
            console.warn('[TokenManager] Refresh failed but token still valid, continuing');
          } else {
            return null;
          }
        }
      }
    }

    // Check idle timeout
    if (this.lastActivity) {
      const idleTimeMs = Date.now() - this.lastActivity.getTime();
      const idleTimeoutMs = this.config.idleTimeoutMinutes * 60 * 1000;
      if (idleTimeMs >= idleTimeoutMs) {
        console.log('[TokenManager] Idle timeout in getToken');
        return null;
      }
    }

    return this.accessToken;
  }

  /**
   * Get the current access token synchronously (no refresh attempt).
   * Used by SDK token providers that require a sync return value.
   */
  getTokenSync(): string | null {
    return this.accessToken;
  }

  /**
   * Refresh the access token
   * FIXED: Added retry logic to prevent logout on temporary network issues
   * FIXED: Better race condition handling with mutex-like pattern
   */
  async refreshToken(): Promise<TokenData> {
    // If refresh is already in progress, return the existing promise
    // This prevents race conditions from multiple simultaneous refresh attempts
    if (this.refreshPromise) {
      console.log('[TokenManager] Refresh already in progress, waiting for it...');
      return this.refreshPromise;
    }

    // Default expiry: 30 minutes from now (matches backend ACCESS_TOKEN_LIFETIME)
    const DEFAULT_EXPIRY_MS = 30 * 60 * 1000;
    const MAX_RETRIES = 2;
    const RETRY_DELAY_MS = 1000;

    const attemptRefresh = async (attempt: number): Promise<TokenData> => {
      try {
        const response = await api.post<TokenData>('/auth/token/refresh/');
        const { access, access_expires_at } = response.data;

        // Defensive handling: if backend doesn't return expires_at, calculate it
        const expiresAt = access_expires_at || new Date(Date.now() + DEFAULT_EXPIRY_MS).toISOString();

        if (!access_expires_at) {
          console.warn('[TokenManager] Backend did not return access_expires_at, using calculated default');
        }

        this.setToken(access, expiresAt);
        return response.data;
      } catch (error: any) {
        // Check if this is a retryable error (network issue, not auth failure)
        const status = error?.response?.status;
        const isAuthFailure = status === 401 || status === 403;
        const isRetryable = !isAuthFailure && attempt < MAX_RETRIES;

        if (isRetryable) {
          console.log(`[TokenManager] Refresh attempt ${attempt} failed, retrying in ${RETRY_DELAY_MS}ms...`);
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY_MS));
          return attemptRefresh(attempt + 1);
        }

        // Non-retryable error or max retries reached
        throw error;
      }
    };

    // Create the refresh promise with retry logic
    this.refreshPromise = attemptRefresh(1)
      .then(result => {
        this.refreshPromise = null;
        return result;
      })
      .catch(error => {
        this.refreshPromise = null;
        // Only logout on definitive auth failures, not network errors
        const status = error?.response?.status;
        if (status === 401 || status === 403) {
          console.log('[TokenManager] Refresh token rejected by server, logging out');
          this.handleExpiredToken();
        } else {
          console.error('[TokenManager] Refresh failed after retries, but not logging out:', error.message);
          // Don't immediately logout on network errors - let the next API call retry
        }
        throw error;
      });

    return this.refreshPromise;
  }

  /**
   * Clear token and stop monitoring
   */
  clearToken() {
    console.log('[TokenManager] clearToken called');

    this.accessToken = null;
    this.expiresAt = null;
    this.lastActivity = null;

    // Clear from localStorage
    this.clearStoredData();

    // Also clear user data when clearing token
    this.clearUserData();

    // Stop expiration monitoring
    if (this.expirationCheckTimer) {
      clearInterval(this.expirationCheckTimer);
      this.expirationCheckTimer = null;
    }

    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }

    this.refreshPromise = null;
  }

  /**
   * Check if token is valid
   * Checks both JWT token expiry AND idle timeout
   */
  isTokenValid(): boolean {
    if (!this.accessToken || !this.expiresAt) {
      return false;
    }

    const now = Date.now();
    const tokenValid = this.expiresAt.getTime() > now;

    if (!tokenValid) {
      console.log('[TokenManager] Token expired:', {
        expiresAt: this.expiresAt.toISOString(),
        now: new Date(now).toISOString()
      });
      return false;
    }

    // Also check idle timeout
    if (this.lastActivity) {
      const idleTimeMs = now - this.lastActivity.getTime();
      const idleTimeoutMs = this.config.idleTimeoutMinutes * 60 * 1000;

      if (idleTimeMs >= idleTimeoutMs) {
        console.log('[TokenManager] Idle timeout exceeded:', {
          idleMinutes: Math.round(idleTimeMs / 1000 / 60),
          timeoutMinutes: this.config.idleTimeoutMinutes
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Get time until token expiration in milliseconds
   */
  getTimeUntilExpiration(): number {
    if (!this.expiresAt) return 0;
    return Math.max(0, this.expiresAt.getTime() - Date.now());
  }

  /**
   * Get idle time in milliseconds
   */
  getIdleTime(): number {
    if (!this.lastActivity) return 0;
    return Date.now() - this.lastActivity.getTime();
  }

  /**
   * Add activity listener
   */
  onActivity(listener: () => void) {
    this.activityListeners.add(listener);
    return () => this.activityListeners.delete(listener);
  }

  /**
   * Emit expiration warning event
   */
  private emitExpirationWarning(secondsRemaining: number) {
    window.dispatchEvent(new CustomEvent('tokenExpirationWarning', {
      detail: { secondsRemaining }
    }));
  }

  /**
   * Update session configuration
   */
  updateConfig(config: Partial<SessionConfig>) {
    this.config = { ...this.config, ...config };
    // Restart monitoring with new config
    this.startExpirationMonitoring();
  }

  /**
   * Get stored user data
   */
  getUserData(): any | null {
    try {
      const storedData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
      if (storedData) {
        return JSON.parse(storedData);
      }
    } catch (e) {
      console.error('[TokenManager] Failed to parse user data:', e);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
    return null;
  }

  /**
   * Set user data
   */
  setUserData(userData: any) {
    try {
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(userData));
    } catch (e) {
      console.error('[TokenManager] Failed to store user data:', e);
    }
  }

  /**
   * Clear user data
   */
  clearUserData() {
    try {
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    } catch (e) {
      console.error('[TokenManager] Failed to clear user data:', e);
    }
  }

  /**
   * Get current idle time in minutes (for debugging/display)
   */
  getIdleTimeMinutes(): number {
    if (!this.lastActivity) return 0;
    return Math.round((Date.now() - this.lastActivity.getTime()) / 1000 / 60);
  }

  /**
   * Get configured idle timeout in minutes
   */
  getIdleTimeoutMinutes(): number {
    return this.config.idleTimeoutMinutes;
  }
}

// Check if we're in a hot reload scenario
const isHotReload = typeof window !== 'undefined' && 
  window.__enhancedTokenManagerInstance && 
  (module as any).hot;

// Export singleton instance
export const enhancedTokenManager = EnhancedTokenManager.getInstance();

// Also expose getInstance for cases where we need fresh reference
export const getEnhancedTokenManager = () => EnhancedTokenManager.getInstance();

// Also expose on window for debugging
if (typeof window !== 'undefined') {
  (window as any).enhancedTokenManager = enhancedTokenManager;
  (window as any).getEnhancedTokenManager = getEnhancedTokenManager;
}

// Log if we're in a hot reload
if (isHotReload) {
  console.log('[TokenManager] Module hot reloaded, using existing instance');
}